import { useState } from 'react';
import { PolylineF, OverlayView, OverlayViewF } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { ThemeType } from '../../../../theme/themeType';
import { useAppTheme } from '../../../../theme/ThemeContext';
import { OVERLAY_Z_IDX, convertToGooglePath } from '../helpers';
import {
  RouteHistoryPoint, RouteHistorySegment, RouteHistoryStop, RouteHistoryTrip,
} from '../routeHistory';
import {
  convertSecToTimeFullLabel, getFormattedDateAndTime, getHoursAndMinutes,
} from '../../../../utils/dateUtils';
import { generateDistance } from '../../../../utils/unitsUtils';
import S from './RouteHistoryStyles';

const centerOffset = (width: number, height: number): { x: number, y: number } => ({
  x: -(width / 2),
  y: -(height / 2),
});

const getTripLabel = (trip: RouteHistoryTrip): string => {
  const period = trip.endAt
    ? `${getHoursAndMinutes(trip.startAt)} - ${getHoursAndMinutes(trip.endAt)}`
    : `${getHoursAndMinutes(trip.startAt)} - ongoing`;
  const distance = trip.distanceInKm != null ? ` · ${generateDistance(trip.distanceInKm)}` : '';
  return `${period}${distance}`;
};

const getStopLabel = (stop: RouteHistoryStop): string => (
  `Stopped for ${convertSecToTimeFullLabel(stop.durationSec, true)}`
);

function RouteHistoryEndpoint({
  point, label, className,
}: {
  point: RouteHistoryPoint;
  label: string;
  className: string;
}): JSX.Element {
  return (
    <OverlayViewF
      position={{ lat: point.position[0], lng: point.position[1] }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      zIndex={OVERLAY_Z_IDX}
      getPixelPositionOffset={centerOffset}
    >
      <S.Endpoint
        className={className}
        title={`${label}: ${getFormattedDateAndTime(new Date(point.timestampSecEpoch * 1000))}`}
      />
    </OverlayViewF>
  );
}

type HoveredTrip = {
  trip: RouteHistoryTrip;
  position: google.maps.LatLngLiteral;
};

/**
 * Draws where the entity has been over the selected period. The path is split per trip and
 * per stop (see buildRouteHistoryPath), so parked time leaves a visible gap with a marker
 * instead of a fake straight line, and each trip leg opens its trip details on click.
 */
function RouteHistory({
  segments, stops, start, end,
}: {
  segments: RouteHistorySegment[];
  stops: RouteHistoryStop[];
  start?: RouteHistoryPoint;
  end?: RouteHistoryPoint;
}): JSX.Element | null {
  const navigate = useNavigate();
  const theme = useTheme() as ThemeType;
  const { themeMode } = useAppTheme();
  const [hovered, setHovered] = useState<HoveredTrip | null>(null);
  const routeStrokeColor = themeMode === 'light' ? theme.colors.text : theme.colors.main_cta;

  const TRIP_STROKE = {
    strokeColor: routeStrokeColor,
    strokeOpacity: 0.85,
    strokeWeight: 3,
  };

  // Points the backend could not attribute to a trip still tell the story of where the
  // entity was, but they are not clickable - drawn thinner and dimmer to say so.
  const ORPHAN_STROKE = {
    strokeColor: theme.colors.text,
    strokeOpacity: 0.6,
    strokeWeight: 2,
  };

  const HIGHLIGHT_STROKE = {
    strokeColor: theme.colors.green,
    strokeOpacity: 1,
    strokeWeight: 5,
  };

  if (!segments.length) return null;

  const openTrip = (trip: RouteHistoryTrip): void => {
    navigate(`/trips/${trip.detailsId}`, { state: { isMergedTrip: trip.isMergedTrip } });
  };

  return (
    <>
      {segments.map((segment) => {
        const isHovered = !!segment.trip && hovered?.trip.detailsId === segment.trip.detailsId;
        let stroke = segment.trip ? TRIP_STROKE : ORPHAN_STROKE;
        if (isHovered) stroke = HIGHLIGHT_STROKE;

        return (
          <PolylineF
            key={segment.key}
            path={convertToGooglePath(segment.path)}
            options={{ ...stroke, clickable: !!segment.trip }}
            onClick={segment.trip ? () => openTrip(segment.trip as RouteHistoryTrip) : undefined}
            onMouseOver={segment.trip ? (e: google.maps.MapMouseEvent) => {
              const position = e.latLng?.toJSON();
              if (position) setHovered({ trip: segment.trip as RouteHistoryTrip, position });
            } : undefined}
            onMouseOut={segment.trip ? () => setHovered(null) : undefined}
          />
        );
      })}
      {stops.map((stop) => (
        <OverlayViewF
          key={stop.key}
          position={{ lat: stop.position[0], lng: stop.position[1] }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          zIndex={OVERLAY_Z_IDX}
          getPixelPositionOffset={centerOffset}
        >
          <S.Stop
            title={`${getStopLabel(stop)} (${getFormattedDateAndTime(new Date(stop.fromSecEpoch * 1000))} - ${getFormattedDateAndTime(new Date(stop.toSecEpoch * 1000))})`}
          />
        </OverlayViewF>
      ))}
      {!!hovered && (
        <OverlayViewF
          position={hovered.position}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          zIndex={OVERLAY_Z_IDX + 1}
          getPixelPositionOffset={(width: number) => ({ x: -(width / 2), y: -48 })}
        >
          <S.TripCard>
            <span className="title">{getTripLabel(hovered.trip)}</span>
            <span className="hint">Click to open trip details</span>
          </S.TripCard>
        </OverlayViewF>
      )}
      {!!start && (
        <RouteHistoryEndpoint point={start} label="Route start" className="start" />
      )}
      {!!end && (
        <RouteHistoryEndpoint point={end} label="Last known position in period" className="end" />
      )}
    </>
  );
}

export default RouteHistory;
